import React, { useEffect, useRef, useState } from "react";
import styles from "../Form.module.scss";

// Stored as a comma-joined string in string_value (underlying field is TEXT_INPUT).
const splitMulti = (s) =>
  (s || "")
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
const joinMulti = (arr) => (arr || []).join(",");

function MultiSelectInput({
  substep,
  value,
  onChange,
  error,
  isReadOnly,
  options = [],
}) {
  const selected = splitMulti(value?.string_value);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const toggle = (val) => {
    if (isReadOnly) return;
    const next = selected.includes(val)
      ? selected.filter((v) => v !== val)
      : [...selected, val];
    onChange(substep.substep_id, { string_value: joinMulti(next) });
  };

  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;
  const showSearch = options.length > 0;
  const selectedLabels = selected.map(
    (selectedValue) =>
      options.find((option) => option.value === selectedValue)?.label ||
      selectedValue
  );

  return (
    <div className={styles.fieldBody} ref={wrapRef}>
      <div className={styles.selectWrap}>
        <button
          type="button"
          className={`${styles.selectBtn} ${error ? styles.inputError : ""}`}
          disabled={isReadOnly}
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span
            className={
              selected.length === 0
                ? styles.selectPlaceholder
                : styles.multiValue
            }
            title={selectedLabels.join(", ")}
          >
            {selected.length === 0
              ? "Select options"
              : selectedLabels.join(", ")}
          </span>
          <span
            className={`${styles.selectCaret} ${
              open ? styles.selectCaretOpen : ""
            }`}
            aria-hidden="true"
          >
            <svg viewBox="0 0 20 20" fill="none">
              <path
                d="M5 7.5 10 12.5 15 7.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>

        {open && (
          <div
            className={styles.selectMenu}
            role="listbox"
            aria-multiselectable="true"
          >
            {showSearch && (
              <input
                className={styles.selectSearch}
                type="text"
                placeholder="Search…"
                value={query}
                autoFocus
                onChange={(e) => setQuery(e.target.value)}
              />
            )}
            <div className={styles.selectMenuScroll}>
              {filtered.length === 0 ? (
                <div className={styles.selectEmpty}>No matches</div>
              ) : (
                filtered.map((opt) => {
                  const isSel = selected.includes(opt.value);
                  return (
                    <div
                      key={opt.value}
                      role="option"
                      aria-selected={isSel}
                      className={`${styles.selectItem} ${
                        isSel ? styles.selectItemSel : ""
                      }`}
                      onClick={() => toggle(opt.value)}
                    >
                      <span>{opt.label}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
      {error && <div className={styles.fieldError}>{error}</div>}
    </div>
  );
}

export default MultiSelectInput;
